import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchFastApi } from "../../api/fastapi";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";

const SUPPORT_EMAIL = "support@midship.dev";

/**
 * Transient page the Docs sidebar link opens in a new tab. It fetches a Zendesk
 * SSO redirect URL from the backend and forwards the signed-in user into the
 * gated Optro Help Center. A `return_to` query param deep-links to a specific
 * article; the backend falls back to the Help Center home if it isn't a valid
 * Help Center URL. If the backend isn't configured or the request fails, it
 * falls back to emailing support so the link is never a dead end.
 */
export const ZendeskRedirect = () => {
  const [failed, setFailed] = useState(false);
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return_to");

  useEffect(() => {
    let cancelled = false;

    const openHelpCenter = async () => {
      try {
        const path = returnTo
          ? `/support/zendesk-sso?return_to=${encodeURIComponent(returnTo)}`
          : "/support/zendesk-sso";
        const data = await fetchFastApi({ method: "GET", path });
        if (cancelled) {
          return;
        }
        if (data?.redirect_url) {
          window.location.replace(data.redirect_url);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    };

    openHelpCenter();

    return () => {
      cancelled = true;
    };
  }, [returnTo]);

  if (failed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900">
            We couldn't open the Help Center
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Please email us at{" "}
            <a
              className="text-blue-600 hover:underline"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <LoadingSpinner />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-gray-900">
          Opening Optro Help Center…
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Please wait while we sign you in.
        </p>
      </div>
    </div>
  );
};
